import React, { useEffect, createRef, useMemo } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import { useTranslate } from '@im/react-localization';
import FormHelperText from '@mui/material/FormHelperText';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import { useBusinessJustificationQuery } from '../../hooks/business-justification';
import { AlertDispatcher } from '../../../../provider/AlertProvider';

type BusinessJustificationProps = {
  countryCode: string;
  error?: string | null;
  onChange: (value: number) => void;
  showAlert: AlertDispatcher;
  value?: number | null;
};

const BusinessJustification = (props: BusinessJustificationProps) => {
  const translate = useTranslate();
  const ref = createRef<any>();

  const businessJustificationsResult = useBusinessJustificationQuery(
    props.countryCode
  );

  const options = useMemo(
    () =>
      businessJustificationsResult.data?.businessJustifications.map((b) => ({
        label: b.name,
        value: b.id,
      })) ?? [],
    [businessJustificationsResult.data]
  );

  useEffect(
    () => {
      businessJustificationsResult.error &&
        props.showAlert({
          text: businessJustificationsResult.error.message,
          type: 'error',
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [businessJustificationsResult.error]
  );

  useEffect(() => {
    if (!props.value && options.length > 0) {
      props.onChange(options[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, options]);

  const handleChange = (event: SelectChangeEvent) => {
    props.onChange(Number(event.target.value));
  };

  return (
    <FormControl error={Boolean(props.error)} fullWidth sx={{ width: '250px' }}>
      <Typography
        component={'label'}
        htmlFor="business-justification-dropdown"
        sx={{
          color: (theme) => theme.palette.text.primary,
          fontSize: '0.875rem',
        }}
      >
        {translate('Pricing.BusinessJustificationPrompt')}
      </Typography>
      <Select
        IconComponent={ExpandMoreOutlinedIcon}
        MenuProps={{
          MenuListProps: {
            autoFocusItem: false,
          },
          TransitionProps: {
            /* istanbul ignore next */
            onEntered: () => ref?.current?.focus(),
          },
          disableAutoFocus: true,
          disablePortal: false,
        }}
        data-testid="dropdown-select"
        inputProps={{
          id: 'business-justification-dropdown',
        }}
        onChange={handleChange}
        sx={{ height: '35px' }}
        value={(props.value as any) ?? options?.[0]?.value ?? ''}
      >
        {options.map((option: any, i: number) => (
          <MenuItem
            key={option.value}
            ref={i === 0 || option.value === props.value ? ref : null}
            value={option.value}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText sx={{ ml: 0 }}>{props.error}</FormHelperText>
    </FormControl>
  );
};

export default BusinessJustification;
